import { datadogLogs } from '@datadog/browser-logs';
import { useMutation } from '@tanstack/react-query';
import { getInclusionChannelsList } from '../../../api/config/growth/v0';
import { postApiFn } from '../../../api/helper';
import { ProxyChannelsListResponse } from '../../../types/proxyApi';

export const QUERY_KEY = 'sample-inclusion-channels';

interface RequestParams {
  page?: number;
  include_concepts?: string[];
  exclude_concepts?: string[];
  threshold?: number;
  limit?: number;
  languages?: string[];
  countries?: string[];
  sort_field?: string;
  sort_dir?: 'asc' | 'desc';
  maximum_risk_level?: number;
}

type Response = ProxyChannelsListResponse;

const useInclusionChannelsListMutation = ({
  page = 1,
  limit = 1000, // 10k is server default if no value provided
  include_concepts = [],
  exclude_concepts = [],
  threshold = 0.7,
  maximum_risk_level = 3,
  // Extracting to safely ignore — these are not forwarded to the request body
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sort_field,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sort_dir,
  languages = [],
  countries = [],
  ...params
}: RequestParams) => {
  const path = getInclusionChannelsList();
  const safePage = Math.max(0, page);
  const offset = limit * safePage;
  return useMutation({
    mutationKey: [
      QUERY_KEY,
      `limit=${limit}`,
      `offset=${offset}`,
      `included_concepts=${include_concepts.join('')}`,
      `excluded_concepts=${exclude_concepts.join('')}`,
      `threshold=${threshold}`,
      `risk=${maximum_risk_level}`,
      `languages=${languages}`,
      `countries=${countries}`,
      `params=${params}`,
    ],
    mutationFn: async () => {
      return await postApiFn(path, {
        ...params,
        languages,
        countries,
        include_concepts,
        exclude_concepts,
        threshold,
        limit,
        offset,
        maximum_risk_level: Math.min(Math.max(0, maximum_risk_level), 3),
      })
        .then((res: Response) => {
          datadogLogs.logger.info('Retrieved results for the channels list', { res });
          return res;
        })
        .catch((error) => {
          datadogLogs.logger.error('Failed to retrieve results for the concepts list', {
            error,
          });
          throw new Error(error);
        });
    },
  });
};

export default useInclusionChannelsListMutation;
